import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import axios from 'axios';

import { useQueryUser } from '@hooks/useQueryUser';
import { useElementWithObserver } from '@hooks/utils/useElementWithObserver';

import { common } from '@store/selectors';
import { useAppSelector } from '@store/store';

import {
	updateAssistantStyle,
	WIDGET_FOOTER_SELECTOR,
} from '@components/common/AIAssistant/helpers/updateAssistantStyle';

import { IKaiAssistantDTO } from '@interfaces/kai/dto/kaiDTO';

const precessAssistantScriptUrl = 'https://cdn.awtg.ai/kai-chat.js';

const UPDATE_DELAY_MS = 300;
const PROCESS_DELAY_MS = 1000;

type ElementStyles = Partial<CSSStyleDeclaration>;

interface InitialStyles {
	container?: ElementStyles;
	labelEl?: ElementStyles;
	buttonIcon?: ElementStyles;
	buttonEl?: ElementStyles;
}

const LABEL_EL_SELECTOR = 'div > div > div > button > div > span';
const BUTTON_ICON_SELECTOR = 'div > div > div > button > div > svg';
const BUTTON_EL_SELECTOR = 'div > div > div > button';

export default AiAssistant;

function AiAssistant({ kaiAssistant }: { kaiAssistant: IKaiAssistantDTO }) {
	const { user } = useQueryUser();
	const { isMapMode, isAdminSidebarOpen } = useAppSelector(common);

	const [kaiId, setKaiId] = useState<string>();
	const [latestContainer, setLatestContainer] = useState<HTMLElement | null>(null);

	const [isKaiChatWindowStateChanges, setIsKaiChatWindowStateChanges] = useState(false);
	const [isKaiScriptLoaded, setIsKaiScriptLoaded] = useState(false);

	const precessAssistantScript = async () => {
		const response = await axios.get(precessAssistantScriptUrl);
		const script = document.createElement('script');

		script.defer = true;
		script.text = response.data;
		document.body.appendChild(script);

		setIsKaiScriptLoaded(true);
	};

	const kaiAssistantComponent = useElementWithObserver({
		selector: '#root > kai-chat',
		isReady: isKaiScriptLoaded,
	});
	const container = useElementWithObserver({
		selector: '.flex.fixed.w-full',
		rootElement: kaiAssistantComponent?.shadowRoot,
		isReady: !!kaiAssistantComponent,
	});

	const initialStylesRef = useRef<InitialStyles | null>(null);
	const labelElRef = useRef<HTMLSpanElement | null>(null);

	useLayoutEffect(() => {
		if (container && !initialStylesRef.current) {
			const labelEl = container.querySelector<HTMLSpanElement>(LABEL_EL_SELECTOR);

			if (labelEl && !labelElRef.current) {
				labelElRef.current = labelEl;
			}

			const buttonIcon = container.querySelector<SVGElement>(BUTTON_ICON_SELECTOR);
			const buttonEl = container.querySelector<HTMLButtonElement>(BUTTON_EL_SELECTOR);

			initialStylesRef.current = {
				container: {
					position: container.style.position,
					left: container.style.left,
					maxWidth: container.style.maxWidth,
					width: container.style.width,
				},
				labelEl: labelEl
					? {
						display: labelEl.style.display,
					}
					: undefined,
				buttonIcon: buttonIcon
					? {
						display: buttonIcon.style.display,
					}
					: undefined,
				buttonEl: buttonEl
					? {
						paddingLeft: buttonEl.style.paddingLeft,
					}
					: undefined,
			};
		}
	}, [container]);

	useEffect(() => {
		const shadowContainer = latestContainer || container;

		if (!shadowContainer) {
			return;
		}

		updateAssistantStyle({
			container: shadowContainer,
			isMapMode,
			isAdminSidebarOpen,
			initialStylesRef,
		});
	}, [container, isMapMode, isAdminSidebarOpen, latestContainer]);

	useEffect(() => {
		const handleClick = (e: MouseEvent) => {
			if (e.target instanceof HTMLElement) {
				if (e.target.tagName.toLowerCase() === 'kai-chat') {
					setIsKaiChatWindowStateChanges(prevState => !prevState);
				}
			}
		};

		document.addEventListener('mousedown', handleClick);

		return () => document.removeEventListener('mousedown', handleClick);
	}, []);

	useEffect(() => {
		const isKaiChatWindowOpen = container?.querySelector(WIDGET_FOOTER_SELECTOR);

		if (isKaiChatWindowOpen) {
			setTimeout(() => {
				updateAssistantStyle({
					container,
					isMapMode,
					isAdminSidebarOpen,
					initialStylesRef,
				});
			}, UPDATE_DELAY_MS);
		}
	}, [container, isAdminSidebarOpen, isKaiChatWindowStateChanges, isMapMode]);

	useEffect(() => {
		if (!kaiAssistant?.isEnabled || kaiId) {
			return;
		}

		setKaiId(kaiAssistant?.kaiId);

		setTimeout(() => {
			void precessAssistantScript();
		}, PROCESS_DELAY_MS);
	}, [user, kaiAssistant, kaiId]);

	useEffect(() => {
		if (!kaiAssistantComponent?.shadowRoot) {
			return;
		}

		const observer = new MutationObserver(() => {
			const updatedContainer = kaiAssistantComponent.shadowRoot?.querySelector('.flex.fixed.w-full') as HTMLElement | null;

			if (updatedContainer) {
				setLatestContainer(updatedContainer);
			}
		});

		observer.observe(kaiAssistantComponent.shadowRoot, {
			childList: true,
			subtree: true,
		});

		return () => observer.disconnect();
	}, [kaiAssistantComponent]);

	return (
		<>
			{/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
			{/* @ts-expect-error*/}
			<kai-chat api-key={kaiId}></kai-chat>
		</>
	);
}
